import { combineLatest, map, startWith } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'

import {
  activeItem$,
  brandMenuActiveItem$,
  headerHovered$,
  headerStyleBase$,
  isHeaderScrolled$,
  showSearch$,
} from './state'
import { HeaderStyle, HeaderStyleState } from './types'

export const defaultHeaderStyle: HeaderStyle = 'white'

export const isValidHeaderStyle = (
  headerStyle: string | null | undefined,
): headerStyle is HeaderStyle => {
  const options = ['lightBlue', 'white', 'transparent'] satisfies HeaderStyle[]
  return !!headerStyle && (options as string[]).includes(headerStyle)
}

interface HeaderStyleInputs {
  isScrolled: boolean
  headerStyleBase: HeaderStyle
  isHovered: boolean
  hasActiveItem: boolean
  /**
   * The search overlay is open. It needs the same treatment as an open menu: a transparent bar sits
   * over page content, so the overlay would otherwise hang off a bar the page shows through.
   */
  isSearchOpen: boolean
}

const resolveHeaderStyle = ({
  isScrolled,
  headerStyleBase,
  isHovered,
  hasActiveItem,
  isSearchOpen,
}: HeaderStyleInputs): HeaderStyle => {
  const shouldForceWhite =
    headerStyleBase === 'transparent' && (isScrolled || isHovered || hasActiveItem || isSearchOpen)

  return shouldForceWhite ? 'white' : headerStyleBase
}

export const getHeaderStyleState = ({
  isScrolled,
  headerStyleBase,
  isHovered,
  hasActiveItem,
  isSearchOpen,
}: HeaderStyleInputs): HeaderStyleState => {
  const headerStyle = resolveHeaderStyle({
    isScrolled,
    headerStyleBase,
    isHovered,
    hasActiveItem,
    isSearchOpen,
  })

  return {
    headerStyleBase,
    headerStyle,
    isSearchOpen,
  }
}

const isSameHeaderStyleState = (a: HeaderStyleState, b: HeaderStyleState): boolean => {
  return (
    a.headerStyleBase === b.headerStyleBase &&
    a.headerStyle === b.headerStyle &&
    a.isSearchOpen === b.isSearchOpen
  )
}

export const headerStyleState$ = combineLatest([
  isHeaderScrolled$,
  headerStyleBase$.pipe(startWith(defaultHeaderStyle)),
  headerHovered$.pipe(startWith(false)),
  activeItem$,
  brandMenuActiveItem$,
  showSearch$,
]).pipe(
  map(([isScrolled, headerStyleBase, isHovered, activeItem, brandMenuActiveItem, isSearchOpen]) =>
    getHeaderStyleState({
      isScrolled,
      headerStyleBase,
      isHovered,
      hasActiveItem: Boolean(activeItem || brandMenuActiveItem),
      isSearchOpen,
    }),
  ),
  distinctUntilChanged(isSameHeaderStyleState),
)
